import { get } from 'svelte/store';
import { z } from 'zod';

import type { MapLayerOneType } from './MapLayerApi';
import { MapLayerApi } from './MapLayerApi';
import { MapLayerInserter } from './MapLayerInserter';
import { MapLegendLayersActive } from '../../shared/MapLegend/MapLegendStore';
import { SettingsApi } from '../../components/Settings/SettingsApi';
import { api } from '../../../stores/api';

export class MapLayerApp {
    private _layers: MapLayerOneType[] = [];
    private _api: MapLayerApi;
    private SETTINGS_ACTIVE_LAYER_KEY = 'MapLegendLayersActive';
    private _initialized: boolean = false;

    constructor(api: MapLayerApi) {
        this._api = api;
    }

    addLayer(layer: MapLayerOneType): this {
        if (!this._layers.some(existingLayer => existingLayer.id === layer.id)) {
            this._layers.push(layer);
        } else {
            console.warn(`Layer with id ${layer.id} already exists.`);
        }
        return this;
    }

    onTurnOnFunction(layerId: string): void {
        if (!this._initialized) {
            console.warn('MapLayerApp is not initialized, cannot turn on layer:', layerId);
            return;
        }
        MapLegendLayersActive.set([...get(MapLegendLayersActive), layerId]);
        this._saveActiveLayers(get(MapLegendLayersActive));
    }

    onTurnOffFunction(layerId: string): void {
        if (!this._initialized) {
            console.warn('MapLayerApp is not initialized, cannot turn off layer:', layerId);
            return;
        }
        MapLegendLayersActive.set(get(MapLegendLayersActive).filter(id => id !== layerId));
        this._saveActiveLayers(get(MapLegendLayersActive));
    }

    reloadLayer(map: any, layerId: string): boolean {
        let layer: MapLayerOneType | undefined = this.getLayerById(layerId);
        if (typeof layer === 'undefined') {
            console.warn(`Layer with id ${layerId} not found.`);
            return false;
        }
        MapLayerInserter.discardLayer(map, layer.id);
        // url example: https://lastmile-test.automapa.pl/api/tiles/tile?x=2277&y=1349&z=12&t=1751536264099&token=e33e7c02ad92fcb704108dc049106ec1&cid=45
        // replace t (timestamp) with current timestamp
        layer.tile_url = layer.tile_url.replace(/t=\d+/, `t=${Date.now()}`);
        console.log('Reloading layer:', layer.id, layer.tile_url);
        MapLayerInserter.insertLayerToMap(
            map,
            layer,
            this.onTurnOnFunction.bind(this),
            this.onTurnOffFunction.bind(this)
        );
        map.modules.Layers.disableLayer(layer.id, true); // disable layer to force reload
        map.modules.Layers.enableLayer(layer.id); // enable layer to reload
        return true;
    }

    getLayerById(layerId: string): MapLayerOneType | undefined {
        return this._layers.find(layer => layer.id === layerId);
    }

    async includeToMap(map: any): Promise<string[]> {
        console.log('MapLayerApp.includeToMap, available layers1:', this.getAvailable());
        try {
            this._initialized = false;
            await this._loadAvailable();
            MapLayerInserter.insertLayersToMap(
                map,
                this._layers,
                this.onTurnOnFunction.bind(this),
                this.onTurnOffFunction.bind(this)
            );
            this._initialized = true;
            console.log('MapLayerApp.includeToMap, available layers2:', this.getAvailable());
            return this._layers.map(layer => layer.id);
        } catch (e) {
            console.error(e);
            return [];
        }
    }

    async _saveActiveLayers(activeLayers: string[]): Promise<void> {
        await SettingsApi.setClientSettings(
            get(api),
            this.SETTINGS_ACTIVE_LAYER_KEY,
            activeLayers,
            z.array(z.string())
        );
    }

    async _loadActiveLayers(): Promise<string[]> {
        const activeLayers = await SettingsApi.getClientSettings<string[]>(
            get(api),
            this.SETTINGS_ACTIVE_LAYER_KEY,
            z.array(z.string())
        ).catch(() => []);
        return activeLayers;
    }

    private async _loadAvailable() {
        this._layers = [...this._layers, ...(await this._api.getAvailable())];
        let activeLayers = await this._loadActiveLayers();
        this._layers.forEach(layer => {
            layer.visible = activeLayers.includes(layer.id);
        });
        MapLegendLayersActive.set(activeLayers);
    }

    public getAvailable(): MapLayerOneType[] {
        return this._layers;
    }
}
